import { Box, CircularProgress, Grid } from "@mui/material";
import React from "react";
import { Helmet } from "react-helmet";
import { getPermissions } from "../../commonFunctions/PermissionManger";
import {
  callAPI,
  IRoleMain,
  MasterPages,
  Method
} from "../../commonFunctions/CommonFunctions";
import { Header, IHeaderActions } from "../../Components/Header/Header";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useDispatch } from "react-redux";
import { resetDataComponent } from "../../StateManagement/actions";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";
import WSSmallGrid from "../../Components/DataGrid/WSSmallGrid";
import RateCardModal from "./RateCardModal";

const RateCardList: React.FC<any> = React.memo((props) => {
  const { showMessage } = useSnackbar();
  const dispatch = useDispatch();
  // const { edit } = getPermissions(MasterPages.Users, IRoleMain.Masters);

  const [loading, setLoading] = React.useState(false);
  const [editedCardName, setEditedCardName] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false); //for create / update
  const [isPopupOpen, setPopupOpen] = React.useState(false); //for delete;

  const [editingCardId, setEditingCardId] = React.useState<string | null>(null);
  const [selectedIdForDlt, setSelectedIdForDlt] = React.useState<any>(null);

  const source = {
    url: "rateCards/search",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: []
  };

  const handleDeleteConfrim = (id: string) => {
    setPopupOpen(true);
    setSelectedIdForDlt(id);
  };

  const handleDelete = () => {
    dispatch(resetDataComponent("clientGrid"));
    showMessage("Rate card deleted successfully!", "success");
  };

  const handleClick = () => {
    setModalOpen(true);
  };

  const handleData = async (formData: any) => {
    setLoading(true);
    try {
      const method = Method.POST;
      const url = "rateCards/create";

      const payload = {
        name: formData.name,
        roles: [
          {
            role: formData.role,
            actualRate: parseFloat(formData.actualRate) || 0,
            customerRate: parseFloat(formData.customerRate) || 0
          }
        ]
      };

      const response: any = await callAPI(url, method, payload);

      if (response.success) {
        showMessage(response?.message, "success");
        dispatch(resetDataComponent("rateCardGrid"));
      }
    } catch (error: any) {
      if (error.message === 'GlobalHandledError') return;
      showMessage(
        error.response?.data.message || "An unexpected error occurred",
        "error"
      );
    } finally {
      setModalOpen(false);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleUpdateCardName = async (cardId: string) => {
    try {
      if (!editedCardName.trim()) return;

      const payload = { name: editedCardName };
      const response = await callAPI(
        `rateCards/update/${cardId}`,
        Method.PUT,
        payload
      );

      if (response.success) {
        showMessage("Rate card name updated", "success");
        dispatch(resetDataComponent("rateCardGrid"));
      }
    } catch (error: any) {
      if (error.message === 'GlobalHandledError') return;
      showMessage(
        error.response?.data.message || "Failed to update name",
        "error"
      );
    } finally {
      setEditingCardId(null);
    }
  };

  const columns = [
    { key: "role", label: "Role Name" },
    { key: "actualRate", label: "Actual Rate (₹/hr)" },
    { key: "customerRate", label: "Customer Rate (₹/hr)" }
  ];

  const action: IHeaderActions = {
    type: "button",
    text: "+ New Rate Card",
    size: "medium",
    onClick: handleClick,
    variant: "contained",
    color: "warning",
    sx: {
      backgroundColor: "#FF6B2C",
      textTransform: "none",
      "&:hover": { backgroundColor: "#e65c1d" }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Rate Cards | Willsoar Technologies</title>
        <meta
          name="description"
          content="Set up custom rate cards for different projects or clients. Manage item pricing and services efficiently."
        />
      </Helmet>
      <Grid p={2}>
        <Header
          type="dashboard"
          title="Roles & Rate Cards"
          id={"rateCardHeader"}
          action={action}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WSSmallGrid
              id="rateCardGrid"
              source={source}
              columns={columns}
              description={
                "Changes to rates here will not update automatically in estimates. To update them manually, go to estimate actions and update items to the current rate card."
              }
              handleUpdate={handleUpdateCardName}
              handleDelete={handleDeleteConfrim}
              cardId={editingCardId}
              cardName={editedCardName}
              setEditedCardName={setEditedCardName}
              setEditingCardId={setEditingCardId}
            />
          </Grid>
        </Grid>

        <RateCardModal
          open={modalOpen}
          onClose={handleClose}
          onCreate={handleData}
        />

        {isPopupOpen && (
          <ConfirmationPopup
            apiUrl={"rateCards/delete"}
            id={selectedIdForDlt}
            confirmMessage="Are you sure you want to delete this rate card?"
            open={isPopupOpen}
            onClose={() => setPopupOpen(false)}
            onConfirm={handleDelete}
            permanent
          />
        )}
      </Grid>
    </>
  );
});


export default RateCardList;